import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";

const PRESETS = [
  { label: "Square · 800", w: 800, h: 800 },
  { label: "16:9 · 1280", w: 1280, h: 720 },
  { label: "9:16 · 1080", w: 1080, h: 1920 },
  { label: "Banner · 1200×400", w: 1200, h: 400 },
  { label: "Default", w: 800, h: 560 },
];

const BACKGROUNDS = [
  { label: "Transparent", value: "transparent" },
  { label: "White", value: "#ffffff" },
  { label: "Black", value: "#0a0a0a" },
  { label: "Klein Blue", value: "#002FA7" },
  { label: "Orange", value: "#FF6B00" },
];

export default function CanvasSettings({ open, onClose, meta, setMeta }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="absolute top-16 left-1/2 z-30 bg-panel border border-line shadow-2xl w-[340px] rounded-2xl overflow-hidden"
          data-testid="canvas-settings-panel"
          initial={{ opacity: 0, scale: 0.94, y: -10, x: "-50%" }}
          animate={{ opacity: 1, scale: 1, y: 0, x: "-50%" }}
          exit={{ opacity: 0, scale: 0.96, y: -8, x: "-50%" }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformOrigin: "top center" }}
        >
      <div className="flex items-center justify-between px-4 h-10 border-b border-line">
        <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-ink3">Canvas Settings</span>
        <button onClick={onClose} className="text-ink4 hover:text-ink" data-testid="settings-close-btn">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="p-4 space-y-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink4 mb-2">Dimensions</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="space-y-1 block">
              <span className="text-[10px] font-mono uppercase text-ink4">Width</span>
              <Input
                type="number"
                value={meta.width}
                onChange={(e) => setMeta({ ...meta, width: Math.max(40, Number(e.target.value) || 0) })}
                data-testid="canvas-width-input"
                className="h-8 rounded-none bg-chrome border-line text-foreground text-sm"
              />
            </label>
            <label className="space-y-1 block">
              <span className="text-[10px] font-mono uppercase text-ink4">Height</span>
              <Input
                type="number"
                value={meta.height}
                onChange={(e) => setMeta({ ...meta, height: Math.max(40, Number(e.target.value) || 0) })}
                data-testid="canvas-height-input"
                className="h-8 rounded-none bg-chrome border-line text-foreground text-sm"
              />
            </label>
          </div>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink4 mb-2">Presets</div>
          <div className="grid grid-cols-2 gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => setMeta({ ...meta, width: p.w, height: p.h })}
                data-testid={`preset-${p.w}x${p.h}-btn`}
                className="text-left text-[11px] font-mono px-2 py-1.5 border border-line text-ink2 hover:bg-hover hover:text-ink hover:border-[#FF6B00]"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink4 mb-2">Export Background</div>
          <div className="flex gap-1.5 flex-wrap">
            {BACKGROUNDS.map((b) => (
              <button
                key={b.value}
                onClick={() => setMeta({ ...meta, background: b.value })}
                data-testid={`bg-${b.label.toLowerCase().replace(/\s+/g, "-")}-btn`}
                title={b.label}
                className={`h-7 px-2 border text-[10px] font-mono uppercase tracking-wider ${
                  meta.background === b.value
                    ? "border-[#FF6B00] bg-[#FF6B00] text-white"
                    : "border-line text-ink3 hover:text-ink hover:bg-hover"
                }`}
              >
                <span
                  className="inline-block w-3 h-3 align-middle mr-1.5 border border-line"
                  style={{ background: b.value === "transparent" ? "repeating-conic-gradient(#333 0% 25%, #555 0% 50%) 50% / 6px 6px" : b.value }}
                />
                {b.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink4 mb-2">Custom Background</div>
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={meta.background && meta.background !== "transparent" ? meta.background : "#000000"}
              onChange={(e) => setMeta({ ...meta, background: e.target.value })}
              data-testid="canvas-bg-input"
              className="w-8 h-8 bg-chrome border border-line cursor-pointer"
            />
            <Input
              value={meta.background || "transparent"}
              onChange={(e) => setMeta({ ...meta, background: e.target.value })}
              className="h-8 rounded-none bg-chrome border-line text-foreground text-sm flex-1 font-mono"
            />
          </div>
        </div>
      </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
